/**
 * On-floor overlay, React edition of ui/Hud.ts's class: the approach prompt
 * when a tool is under the cursor, and a status strip that keeps the process
 * visible while the student walks elsewhere.
 *
 * Split personality by design. The STRUCTURE is React and its visibility is
 * a projection (hidden while a panel is open); the per-frame CONTENT -
 * target card text, status numbers - is written imperatively through
 * hudBridge, because it changes at frame rate and must never render React.
 * A language or input-modality switch replays the last target so the card
 * re-words itself without waiting for the pointer to move.
 */

import { useEffect, useRef } from 'react'
import {
  toolBlurb,
  toolName,
  toolProcess,
  type Tool,
} from '../../fab/layout'
import { t } from '../../i18n/i18n'
import { touchInput } from '../../ui/inputMode'
import { useLang } from '../i18n/LanguageProvider'
import { useInputMode, useOpenTool } from '../state/hooks'
import { hudBridge, openButtonBridge, type HudCard } from './hudBridge'

export function Hud() {
  const lang = useLang()
  const touch = useInputMode()
  const inTool = useOpenTool() !== null

  const approachRef = useRef<HTMLDivElement>(null)
  const tagRef = useRef<HTMLDivElement>(null)
  const nameRef = useRef<HTMLDivElement>(null)
  const blurbRef = useRef<HTMLDivElement>(null)
  const keyRef = useRef<HTMLDivElement>(null)
  const statusRef = useRef<HTMLDivElement>(null)
  const lastTarget = useRef<Tool | null>(null)

  useEffect(() => {
    // The card as last written, so a caller that sets it every frame (a
    // vehicle's live status) touches the DOM only when a word changes.
    let shown: HudCard | null = null
    const setCard = (card: HudCard | null): void => {
      const approach = approachRef.current
      if (!approach) return
      if (!card) {
        if (shown) approach.classList.remove('visible')
        shown = null
        return
      }
      if (!shown) approach.classList.add('visible')
      if (card.tag !== shown?.tag) tagRef.current!.textContent = card.tag
      if (card.name !== shown?.name) nameRef.current!.textContent = card.name
      if (card.blurb !== shown?.blurb) blurbRef.current!.textContent = card.blurb
      // Locale-only markup (<kbd>) - the same policy as LocaleHtml, written
      // imperatively because this runs per frame.
      if (card.keyHtml !== shown?.keyHtml) keyRef.current!.innerHTML = card.keyHtml
      shown = card
    }
    const setTarget = (tool: Tool | null): void => {
      lastTarget.current = tool
      if (!tool) {
        setCard(null)
        return
      }
      setCard({
        tag: `${tool.assetTag} · ${toolProcess(tool)}`,
        name: toolName(tool),
        blurb: toolBlurb(tool),
        // "Press E" on a phone names a key that does not exist; the wording
        // follows the hand.
        keyHtml:
          tool.status === 'live'
            ? t(touchInput() ? 'hud.OpenLiveTouch' : 'hud.OpenLive')
            : t(touchInput() ? 'hud.OpenPendingTouch' : 'hud.OpenPending'),
      })
    }
    const setStatus = (parts: Array<[string, string]>): void => {
      const status = statusRef.current
      if (!status) return
      status.replaceChildren(
        ...parts.map(([k, v]) => {
          const span = document.createElement('span')
          span.append(`${k} `)
          const b = document.createElement('b')
          b.textContent = v
          span.appendChild(b)
          return span
        }),
      )
    }
    hudBridge.bind({ setTarget, setCard, setStatus })
    return () => hudBridge.bind(null)
  }, [])

  // The words under the card changed (language or hand); replay the target.
  useEffect(() => {
    hudBridge.setTarget(lastTarget.current)
  }, [lang, touch])

  return (
    <div className="hud" style={inTool ? { display: 'none' } : undefined}>
      <div ref={approachRef} className="approach">
        <div ref={tagRef} className="tag" />
        <div ref={nameRef} className="name" />
        <div ref={blurbRef} className="blurb" />
        <div ref={keyRef} className="key" />
      </div>
      <div ref={statusRef} className="status-bar" />
    </div>
  )
}

/** The floating OPEN affordance, placed every frame over the tool's screen. */
export function OpenToolButton() {
  useLang()
  const ref = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    openButtonBind(ref.current)
    return () => openButtonBind(null)
  }, [])

  return (
    <button
      ref={ref}
      className="open-tool-btn"
      onClick={() => openButtonBridge.onPress?.()}
    >
      {t('hud.Open')} <kbd>E</kbd>
    </button>
  )
}


function openButtonBind(button: HTMLButtonElement | null): void {
  if (!button) {
    openButtonBridge.bind(null)
    return
  }
  openButtonBridge.bind({
    show(x, y) {
      button.style.left = `${x}px`
      button.style.top = `${y}px`
      button.classList.add('visible')
    },
    hide() {
      button.classList.remove('visible')
    },
  })
}
